import { useMemo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

import { useAuth } from '../../../core/authContext';
import { placeholderChartModel } from '../districtStatsChart';
import { tileRows } from '../districtStatsTiles';
import { CARD_SX, DASH } from '../tokens';
import { CardHead, StatTile } from './DistrictStatsCard';
import { StudentsByGradeChart } from './StudentsByGradeChart';

export const DISTRICT_STATS_COMING_SOON = 'Your district stats are coming soon. Stay tuned!';
export const DISTRICT_STATS_UNAVAILABLE = "We couldn't load your district stats.";

// The District Stats card with no stats to draw: the district name from the session, a dash in every tile and a
// grey placeholder chart. With onRetry the strip is the failed request's error and its Try again; without it,
// the coming-soon strip.
export function DistrictStatsComingSoonCard({
  checklist,
  now = new Date(),
  onRetry,
}: {
  checklist?: ReactNode;
  now?: Date;
  onRetry?: () => void;
}) {
  const { district } = useAuth();
  const rows = useMemo(() => tileRows({ districtName: '', sisConnected: false, flashlightConnected: false, grades: [] }, now), [now]);
  const model = useMemo(() => placeholderChartModel(), []);
  return (
    <Box
      component="section"
      role="region"
      aria-label="District stats"
      data-testid="district-stats-coming-soon"
      sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      {checklist}
      <CardHead districtName={district?.name ?? ''} />
      {onRetry ? (
        <Box
          role="alert"
          sx={{
            padding: '8px 12px',
            borderRadius: '4px',
            bgcolor: DASH.stats.comingSoonBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: DASH.stats.comingSoonFg }}>
            {DISTRICT_STATS_UNAVAILABLE}
          </Typography>
          <Button variant="outlined" size="small" onClick={onRetry}>
            Try again
          </Button>
        </Box>
      ) : (
        <Box
          role="status"
          sx={{ padding: '4px 8px', borderRadius: '4px', bgcolor: DASH.stats.comingSoonBg, textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: DASH.stats.comingSoonFg }}>
            {DISTRICT_STATS_COMING_SOON}
          </Typography>
        </Box>
      )}
      {rows.map((row, index) => (
        <Box key={index} data-testid="stat-tile-row" sx={{ display: 'flex', gap: '20px' }}>
          {row.map((tile) => (
            <StatTile key={tile.label} tile={tile} />
          ))}
        </Box>
      ))}
      <StudentsByGradeChart model={model} view="fl" onViewChange={() => {}} />
    </Box>
  );
}
